import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  createTask,
  fetchAttempts,
  fetchFailureReasonCodes,
  fetchTasks,
} from '../api/client';
import type { DeliveryAttempt, FieldTask } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { buildReplacementTask } from '../lib/dashboard-filters';
import { breakdownByReason, isSuccessfulAttempt } from '../lib/delivery-board';
import { reasonCodeLabel } from '../lib/delivery-form';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import { hasAnyPermission } from '../lib/navigation';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Failure Review — mengubah kegagalan menjadi tindakan.
 *
 * Dua pertanyaan yang dijawab layar ini: alasan apa yang paling sering berulang (bahan
 * perbaikan operasional), dan tugas mana yang perlu diulang (tindakan hari ini).
 */
export function FailureReviewPage(): ReactNode {
  const session = useSession();
  const navigate = useNavigate();
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const { state, reload } = useAsync(async () => {
    const [tasks, codes] = await Promise.all([
      fetchTasks(session.auth, 'failed'),
      fetchFailureReasonCodes(session.auth),
    ]);
    const attemptLists = await Promise.all(
      tasks.map((t) =>
        fetchAttempts(session.auth, t.id).catch((): readonly DeliveryAttempt[] => []),
      ),
    );
    return {
      tasks,
      codes,
      attemptsByTask: new Map(tasks.map((t, index) => [t.id, attemptLists[index] ?? []])),
      allAttempts: attemptLists.flat(),
    };
  }, [session.auth]);

  const canCreate = hasAnyPermission(session.me.permissions, ['workforce.task.create']);

  async function createReplacement(task: FieldTask, taskTypeCode: string): Promise<void> {
    setBusyId(task.id);
    setError(null);
    try {
      const draft = buildReplacementTask({
        id: task.id,
        taskTypeCode,
        title: task.title,
        priority: task.priority,
        destinationName: task.destinationName,
        destinationAddress: task.destinationAddress,
        instructions: task.instructions,
      });
      const created = await createTask(session.auth, keys.current(), {
        taskTypeCode: draft.taskTypeCode,
        title: draft.title,
        priority: draft.priority,
        destination:
          draft.destinationName || draft.destinationAddress
            ? { name: draft.destinationName, address: draft.destinationAddress }
            : null,
        instructions: draft.instructions,
        // Tanpa ini tugas pengganti terlihat sebagai pekerjaan baru dan hubungan
        // sebab-akibatnya hilang dari laporan.
        retryOfTaskId: draft.retryOfTaskId,
      });
      keys.rotate();
      navigate(`/tasks/${created.task.id}`);
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setBusyId(null);
    }
  }

  if (state.status === 'loading') return <LoadingState label="Memuat kegagalan…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const { tasks, codes, attemptsByTask, allAttempts } = state.data;
  const breakdown = breakdownByReason(
    allAttempts.filter((a) => !isSuccessfulAttempt(a.outcome)),
    codes,
  );

  return (
    <>
      <section className="card">
        <h2>Failure Review</h2>
        <p className="muted small">
          Alasan yang paling sering berulang adalah tempat perbaikan operasional paling murah.
        </p>
      </section>

      <section className="card">
        <h3>Sebaran alasan gagal</h3>
        {breakdown.length === 0 ? (
          <p className="muted small">Belum ada kegagalan tercatat.</p>
        ) : (
          <table className="compare">
            <thead>
              <tr>
                <th scope="col">Alasan</th>
                <th scope="col">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row) => (
                <tr key={row.reasonCodeId ?? 'none'}>
                  <th scope="row">{row.label}</th>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {error ? <ErrorCard error={error} /> : null}

      <section className="card">
        <h3>Tugas gagal</h3>
        {tasks.length === 0 ? (
          <EmptyState
            title="Tidak ada tugas gagal"
            hint="Semua pengiriman berjalan tanpa kegagalan."
          />
        ) : (
          <ul className="list">
            {tasks.map((task) => {
              const attempts = attemptsByTask.get(task.id) ?? [];
              const last = [...attempts].sort((a, b) => b.attemptNo - a.attemptNo)[0];
              return (
                <li key={task.id} className="list-item">
                  <Link to={`/tasks/${task.id}`}>
                    <strong>{task.title}</strong>
                  </Link>
                  <p className="muted small">{task.taskNumber}</p>
                  {last ? (
                    <p className="small">
                      Percobaan #{last.attemptNo} ·{' '}
                      {reasonCodeLabel(codes, last.failureReasonCodeId)}
                    </p>
                  ) : null}
                  {last?.failureDetail ? <p className="muted small">{last.failureDetail}</p> : null}
                  {task.retryOfTaskId ? (
                    <p className="muted small">Tugas ini sendiri adalah pengganti.</p>
                  ) : null}
                  {canCreate ? (
                    <button
                      type="button"
                      disabled={busyId === task.id}
                      onClick={() => void createReplacement(task, 'courier_delivery')}
                    >
                      {busyId === task.id ? 'Membuat…' : 'Buat tugas pengganti'}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
